import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Divider,
  CircularProgress,
  Typography,
  Button,
  Fab,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import TaskIcon from "@mui/icons-material/Task";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ruRU, enUS } from "@mui/x-date-pickers/locales";
import "dayjs/locale/ru";
import "dayjs/locale/en";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTranslation } from "react-i18next";
import { SelectChangeEvent } from "@mui/material/Select";
import { Project, ProjectMember, ProjectBoard, Account } from "../types";
import {
  getProjectById,
  getProjectMembers,
  createProject,
  getProjectBoards,
  getAccounts,
  projectService,
  updateProject,
} from "../services/projectService";
import ProjectHeader from "../components/ProjectHeader";
import ProjectForm from "../components/ProjectForm";
import ProjectView from "../components/ProjectView";
import ProjectMembersAccordion from "../components/Accordions/ProjectMembersAccordion";
import AttachmentsAccordion from "../components/Accordions/AttachmentsAccordion";
import Notification from "../components/Common/Notification";
import DraftPromptDialog from "../components/Common/DraftPromptDialog";
import {
  buildDraftKey,
  clearDraft,
  loadDraft,
  saveDraft,
} from "../utils/draftStorage";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("ru");

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const defaultUserId = user?.id || "";

  const [project, setProject] = useState<Project | null>(null);
  const [isEditing, setIsEditing] = useState(id === "new");
  const [form, setForm] = useState<Partial<Project>>({
    name: "",
    status: "",
    dateStart: null,
    dateEnd: null,
    description: null,
    boardId: "",
    accountId: null,
    cProgres: null,
    enableCollaborators: false,
    enableHours: true,
    enablePoints: false,
    enablePriority: true,
    enableTaskDependency: true,
    ganttView: true,
    createdById: defaultUserId,
  });
  const [boards, setBoards] = useState<ProjectBoard[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [projectStatuses, setProjectStatuses] = useState<string[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [isLoading, setIsLoading] = useState(id !== "new");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [progressError, setProgressError] = useState<string | null>(null);
  const [draftPromptOpen, setDraftPromptOpen] = useState(false);
  const [draftToRestore, setDraftToRestore] = useState<Partial<Project> | null>(
    null,
  );
  const draftPromptShownRef = useRef(false);

  const draftKey = buildDraftKey("project");

  const loadCreationData = useCallback(async () => {
    if (boards.length > 0 && accounts.length > 0) {
      return;
    }

    try {
      const [boardsData, accountsData] = await Promise.all([
        getProjectBoards(),
        getAccounts(),
      ]);
      setBoards(boardsData.list || []);
      setAccounts(accountsData.list || []);
    } catch (err: any) {
      console.error("Failed to load creation data:", err);
      if (id === "new") {
        setError(
          t("projects.loadCreationDataError") ||
            "Не удалось загрузить данные для создания",
        );
      }
    }
  }, [t, boards.length, accounts.length, id]);

  const initNewForm = useCallback(() => {
    setForm({
      name: "",
      status: "",
      dateStart: null,
      dateEnd: null,
      description: null,
      boardId: "",
      accountId: null,
      cProgres: null,
      enableCollaborators: false,
      enableHours: true,
      enablePoints: false,
      enablePriority: true,
      enableTaskDependency: true,
      ganttView: true,
      createdById: defaultUserId,
    });
    setIsEditing(true);
  }, [defaultUserId]);

  const loadAll = useCallback(async () => {
    if (!id || id === "new") return;
    setIsLoading(true);
    setError(null);
    try {
      const [projectData, membersData] = await Promise.all([
        getProjectById(id),
        getProjectMembers(id),
      ]);

      setProject(projectData);

      setForm({
        name: projectData.name || "",
        status: projectData.status || "",
        dateStart: projectData.dateStart || null,
        dateEnd: projectData.dateEnd || null,
        description: projectData.description || null,
        boardId: projectData.boardId || "",
        accountId: projectData.accountId || null,
        cProgres: projectData.cProgres ?? 0,
      });

      setMembers(membersData.list || []);

      setError(null);
    } catch (err: any) {
      console.error("Failed to load project:", err);
      if (err?.response?.status !== 403) {
        setError(t("projects.loadError") || "Не удалось загрузить проект");
      }
    } finally {
      setIsLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const { getCachedStatuses } = await import("../storage/preloadData");
        const statuses = await getCachedStatuses();
        console.log("Loaded project statuses:", statuses.projects);
        setProjectStatuses(statuses.projects || []);
      } catch (err) {
        console.error("Failed to load project statuses:", err);
        setProjectStatuses([]);
      }
    };
    loadStatuses();
  }, []);

  useEffect(() => {
    if (id === "new" && projectStatuses.length > 0 && !form.status) {
      setForm((prev) => ({
        ...prev,
        status: projectStatuses[0],
      }));
    }
  }, [id, projectStatuses, form.status]);

  useEffect(() => {
    setError(null);
    setIsLoading(true);

    if (id === "new") {
      setIsLoading(false);
      loadCreationData();
    } else {
      loadAll().then(() => {
        loadCreationData();
      });
    }
  }, [id, loadAll, loadCreationData]);

  useEffect(() => {
    draftPromptShownRef.current = false;
    setDraftPromptOpen(false);
    setDraftToRestore(null);
  }, [id]);

  useEffect(() => {
    if (id !== "new") return;
    if (draftPromptShownRef.current) return;
    const defaultStatus = projectStatuses[0] || "";
    const run = async () => {
      const draft = await loadDraft<Partial<Project>>(draftKey);
      if (!draft?.data) return;

      const hasContent =
        !!draft.data.name ||
        !!draft.data.description ||
        !!draft.data.dateStart ||
        !!draft.data.dateEnd ||
        !!draft.data.boardId ||
        !!draft.data.accountId ||
        (!!defaultStatus &&
          draft.data.status &&
          draft.data.status.length > 0 &&
          draft.data.status !== defaultStatus);

      if (!hasContent) {
        await clearDraft(draftKey);
        return;
      }

      setDraftToRestore(draft.data);
      setDraftPromptOpen(true);
      draftPromptShownRef.current = true;
    };
    void run();
  }, [id, draftKey, projectStatuses]);

  useEffect(() => {
    if (id !== "new") return;
    if (draftPromptOpen) return;
    const defaultStatus = projectStatuses[0] || "";
    const shouldSave =
      !!form.name ||
      !!form.description ||
      !!form.dateStart ||
      !!form.dateEnd ||
      !!form.boardId ||
      !!form.accountId ||
      (!!defaultStatus &&
        form.status &&
        form.status.length > 0 &&
        form.status !== defaultStatus);

    if (!shouldSave) {
      clearDraft(draftKey);
      return;
    }

    saveDraft(draftKey, form);
  }, [form, id, draftKey, draftPromptOpen, projectStatuses]);

  const handleUseDraft = () => {
    if (!draftToRestore) return;
    setForm((prev) => ({
      ...prev,
      ...draftToRestore,
      createdById: draftToRestore.createdById || prev.createdById,
    }));
    setIsEditing(true);
    setDraftPromptOpen(false);
  };

  const handleStartNew = () => {
    clearDraft(draftKey);
    initNewForm();
    setDraftPromptOpen(false);
  };

  const handleRefresh = useCallback(async () => {
    if (id !== "new") {
      await loadAll();
    }
  }, [id, loadAll]);

  const handleTextChange =
    (field: keyof Partial<Project>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSelectChange =
    (field: keyof Partial<Project>) => (e: SelectChangeEvent<string>) => {
      const value = e.target.value;
      if (field === "boardId") {
        const selectedBoard = boards.find((b) => b.id === value);
        setForm((prev) => ({
          ...prev,
          boardId: value,
          boardName: selectedBoard?.name || "",
        }));
      } else if (field === "accountId") {
        const selectedAccount = accounts.find((a) => a.id === value);
        setForm((prev) => ({
          ...prev,
          accountId: value || null,
          accountName: selectedAccount?.name || null,
        }));
      } else {
        setForm((prev) => ({ ...prev, [field]: value }));
      }
    };

  const handleNumberChange =
    (field: keyof Partial<Project>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const numValue = value === "" ? null : Number(value);

      if (field === "cProgres" && numValue !== null && numValue > 100) {
        setProgressError(
          t("projects.detail.invalidProgress") ||
            "Прогресс не может быть больше 100%",
        );
      } else if (field === "cProgres") {
        setProgressError(null);
      }

      setForm((prev) => ({
        ...prev,
        [field]: numValue,
      }));
    };

  const handleDateChange =
    (field: keyof Partial<Project>) => (newValue: Dayjs | null) => {
      const dateValue = newValue ? newValue.format("YYYY-MM-DD") : null;
      setForm((prev) => ({ ...prev, [field]: dateValue }));
    };

  const handleSave = async () => {
    setIsLoading(true);
    setError(null);
    setProgressError(null);
    try {
      if (id === "new") {
        const missingFields: string[] = [];
        if (!form.name) {
          missingFields.push(t("projects.detail.nameLabel") || "Название");
        }
        if (!form.boardId) {
          missingFields.push(t("projects.detail.boardLabel") || "Доска");
        }

        if (missingFields.length > 0) {
          const errorMessage =
            missingFields.length === 1
              ? `${missingFields[0]} ${t("projects.detail.required") || "обязательно"}`
              : `${missingFields.join(", ")} ${t("projects.detail.requiredPlural") || "обязательны"}`;
          setError(errorMessage);
          setIsLoading(false);
          return;
        }

        if (
          form.cProgres !== null &&
          form.cProgres !== undefined &&
          form.cProgres > 100
        ) {
          setError(
            t("projects.detail.invalidProgress") ||
              "Прогресс не может быть больше 100%",
          );
          setIsLoading(false);
          return;
        }

        const selectedBoard = boards.find((b) => b.id === form.boardId);
        const selectedAccount = accounts.find((a) => a.id === form.accountId);

        const projectData: Partial<Project> = {
          name: form.name,
          status: form.status || projectStatuses[0] || "",
          boardId: form.boardId,
          boardName: selectedBoard?.name || "",
          dateStart: form.dateStart,
          dateEnd: form.dateEnd,
          description: form.description,
          accountId: form.accountId || null,
          accountName: selectedAccount?.name || null,
          cProgres: form.cProgres ?? null,
          enableCollaborators: form.enableCollaborators ?? false,
          enableHours: form.enableHours ?? true,
          enablePoints: form.enablePoints ?? false,
          enablePriority: form.enablePriority ?? true,
          enableTaskDependency: form.enableTaskDependency ?? true,
          ganttView: form.ganttView ?? true,
          createdById: defaultUserId,
        };

        const created = await createProject(projectData);
        setSuccess(
          t("projects.detail.createSuccess") || "Проект успешно создан",
        );
        setIsEditing(false);
        navigate(`/project/${created.id}`);
        clearDraft(draftKey);
      } else if (id) {
        if (
          form.cProgres !== null &&
          form.cProgres !== undefined &&
          form.cProgres > 100
        ) {
          setError(
            t("projects.detail.invalidProgress") ||
              "Прогресс не может быть больше 100%",
          );
          setIsLoading(false);
          return;
        }

        const selectedBoard = boards.find((b) => b.id === form.boardId);
        const selectedAccount = accounts.find((a) => a.id === form.accountId);

        await projectService.update(id, {
          name: form.name,
          status: form.status,
          dateStart: form.dateStart,
          dateEnd: form.dateEnd,
          description: form.description,
          boardId: form.boardId,
          boardName: selectedBoard?.name || "",
          accountId: form.accountId || null,
          accountName: selectedAccount?.name || null,
          cProgres: form.cProgres ?? null,
        });
        setSuccess(
          t("projects.detail.updateSuccess") || "Проект успешно обновлен",
        );
        setIsEditing(false);
        await handleRefresh();
      }
    } catch (err: any) {
      console.error("Failed to save project:", err);
      const message = err?.response?.data?.message || err?.message;
      setError(
        message ||
          (id === "new"
            ? t("projects.detail.createError") || "Не удалось создать проект"
            : t("projects.detail.updateError") || "Не удалось обновить проект"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await projectService.delete(id);
      const { removeFromCache, generateCacheKey } =
        await import("../storage/cacheManager");
      const projectCacheKey = generateCacheKey(`/Project/${id}`, {});
      await removeFromCache(projectCacheKey);

      const allPossibleKeys = [
        `/Project/${id}`,
        `Project/${id}`,
        `/Project/${id}?`,
      ];
      for (const key of allPossibleKeys) {
        try {
          await removeFromCache(key);
        } catch (e) {}
      }

      setSuccess(t("projects.detail.deleteSuccess") || "Проект успешно удален");

      setTimeout(() => {
        navigate("/projects");
      }, 500);
    } catch (err: any) {
      console.error("Failed to delete project:", err);
      if (
        err?.response?.data?.messageTranslation?.label ===
        "cannotRemoveUsedInTask"
      ) {
        setError(
          t("projects.detail.cannotDeleteUsedInTask") ||
            "Нельзя удалить проект так как к нему прикреплены задачи",
        );
      } else {
        setError(
          err?.response?.data?.message ||
            t("projects.detail.deleteError") ||
            "Не удалось удалить проект",
        );
      }
      throw err;
    }
  };

  const handleCancel = () => {
    if (id === "new") {
      navigate("/projects");
    } else if (project) {
      setForm({
        name: project.name || "",
        status: project.status || "",
        dateStart: project.dateStart || null,
        dateEnd: project.dateEnd || null,
        description: project.description || null,
        boardId: project.boardId || "",
        accountId: project.accountId || null,
        cProgres: project.cProgres ?? 0,
      });
      setIsEditing(false);
    }
    setError(null);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const locale = i18n.language === "en_US" ? enUS : ruRU;

  const handleCloseNotification = () => {
    setError(null);
    setSuccess(null);
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return;
    try {
      await updateProject(id, { status: newStatus });
      setSuccess(t("statusChangeModal.success") || "Статус успешно изменен");
      const { invalidateListCache } = await import("../storage/cacheManager");
      await invalidateListCache("projects");
      await handleRefresh();
    } catch (err: any) {
      setError(err.message || "Ошибка при обновлении статуса проекта.");
    }
  };

  if (isLoading && !project && id !== "new") {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          zIndex: 9999,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!project && id !== "new" && !isLoading) {
    return (
      <Container sx={{ px: { xs: 0, sm: 2 } }}>
        <Box sx={{ mt: 2 }}>
          <Button
            startIcon={<ArrowBackIosNewIcon />}
            onClick={() => navigate("/projects")}
            sx={{
              textTransform: "none",
              px: "0",
              color: "#757575",
              minWidth: "auto",
              mb: 2,
            }}
          >
            {t("tasks.detail.backButton")}
          </Button>
          <Typography variant="h6" color="error">
            {error || t("projects.loadError") || "Проект не найден"}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={i18n.language === "en_US" ? "en" : "ru"}
      localeText={
        locale.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      <Container sx={{ px: { xs: 0, sm: 2 } }}>
        <ProjectHeader
          project={
            project ||
            (id === "new"
              ? ({
                  id: "new",
                  name: t("projects.detail.newProject") || "Новый проект",
                } as Project)
              : null)
          }
          isEditing={isEditing}
          anchorEl={anchorEl}
          handleOpenMenu={handleOpenMenu}
          handleCloseMenu={handleCloseMenu}
          setIsEditing={setIsEditing}
          handleDelete={handleDelete}
          handleCancel={handleCancel}
          handleSave={handleSave}
          error={error}
          setError={setError}
          success={success}
          setSuccess={setSuccess}
          isLoading={isLoading}
        />
        <Divider sx={{ mb: 2 }} />
        {isEditing || id === "new" ? (
          <ProjectForm
            form={form}
            isEditing={isEditing || id === "new"}
            isNew={id === "new"}
            boards={boards}
            accounts={accounts}
            projectStatuses={projectStatuses}
            handleTextChange={handleTextChange}
            handleSelectChange={handleSelectChange}
            handleDateChange={handleDateChange}
            handleNumberChange={handleNumberChange}
            progressError={progressError}
          />
        ) : project ? (
          <ProjectView
            project={project}
            onStatusChange={handleStatusChange}
            onStatusUpdateSuccess={(message) => setSuccess(message)}
          />
        ) : null}
        {id !== "new" && !isEditing && (
          <Box sx={{ mt: 4 }}>
            <ProjectMembersAccordion members={members} />
            <AttachmentsAccordion
              parentId={id!}
              onRefresh={handleRefresh}
              setNotification={(type: "success" | "error", message: string) => {
                if (type === "success") {
                  setSuccess(message);
                } else {
                  setError(message);
                }
              }}
              parentType="Project"
            />
          </Box>
        )}
        <Notification
          open={!!error && !isLoading}
          message={error || ""}
          severity="error"
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
        <Notification
          open={!!success}
          message={success || ""}
          severity="success"
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
        <DraftPromptDialog
          open={draftPromptOpen}
          onUseDraft={handleUseDraft}
          onStartNew={handleStartNew}
        />
        {id !== "new" && (
          <Fab
            color="primary"
            size="small"
            onClick={() => navigate(`/project/${id}/tasks`)}
            sx={{
              position: "fixed",
              bottom: { xs: 62, sm: 24 },
              right: 16,
              border: "1px solid #4467A5",
              borderRadius: "8px",
              bgcolor: "#4467A5",
              color: "#fff",
              "&:hover": { bgcolor: "#385894" },
              boxShadow: "0px 2px 3.6px rgba(0, 0, 0, 0.36)",
              textTransform: "none",
              "@media (min-width: 600px)": {
                minWidth: 180,
                height: 48,
                px: 3,
              },
            }}
            aria-label={
              t("projects.detail.projectTasksButton") || "Задачи проекта"
            }
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0, sm: 1.5 },
              }}
            >
              <TaskIcon />
              <Typography
                sx={{
                  display: { xs: "none", sm: "block" },
                  fontSize: "0.9375rem",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {t("projects.detail.projectTasksButton") || "Задачи проекта"}
              </Typography>
            </Box>
          </Fab>
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default ProjectDetail;
